import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, Sparkles, ShieldCheck, ArrowUpRight, AlertCircle } from "lucide-react";
import { http, formatBytes, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * RetentionForecastWidget — projects how much storage the current
 * tenant's retention policy will reclaim over the next 30/60/90 days.
 *
 * Three modes:
 *  1. No active policy → friendly empty state + "Apply SOC2 defaults"
 *     button (admins) or a "Configure retention" link (everyone else).
 *  2. Active policy, defaults already applied → just the forecast cards.
 *  3. Active policy, but room to improve → forecast cards + an "Apply
 *     SOC2 defaults" sidebar CTA that previews the additional savings.
 *
 * The widget is rendered on /dashboard for any logged-in user (engineer+
 * can preview, admin gets the actions). It silently hides on a 403 in
 * case a future role is more restrictive.
 */

const CLASS_TINTS = {
    file_versions: "#5b5ef0",
    audit_logs: "#00C2FF",
    alerts: "#f59e0b",
    login_attempts: "#6b7280",
    completed_migrations: "#22c55e",
};

export default function RetentionForecastWidget() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    const [forecast, setForecast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [applying, setApplying] = useState(false);

    const load = async () => {
        try {
            const { data } = await http.get("/settings/retention/forecast?horizons=30,60,90");
            setForecast(data);
        } catch (e) {
            // 403 = role too low for retention.engineer+ — hide silently.
            if (e?.response?.status === 403) {
                setErr("hidden");
            } else {
                setErr(errMsg(e) || "Could not load retention forecast");
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const applyDefaults = async () => {
        if (!window.confirm(
            "Apply SOC2-friendly retention defaults?\n\n"
            + "• File versions — keep last 30, expire after 90 days\n"
            + "• Audit logs — 365 days (compliance window)\n"
            + "• Resolved alerts — 90 days\n"
            + "• Login attempts — 14 days\n"
            + "• Completed migrations — 180 days\n\n"
            + "You can fine-tune any class afterwards on /retention."
        )) return;
        setApplying(true);
        try {
            await http.post("/settings/retention/apply-defaults");
            toast.success("SOC2 retention defaults applied across all 5 classes.");
            await load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setApplying(false);
        }
    };

    if (err === "hidden") return null;
    if (loading) {
        return (
            <div className="mb-card" data-testid="retention-forecast-loading">
                <div className="h-24 animate-pulse rounded-md bg-muted/20" />
            </div>
        );
    }
    if (err) {
        return (
            <div className="mb-card flex items-center gap-2 text-xs text-muted-foreground" data-testid="retention-forecast-error">
                <AlertCircle size={13} /> {err}
            </div>
        );
    }
    if (!forecast) return null;

    const { horizons, per_class, totals, has_active_policy, suggested_defaults_available, class_meta } = forecast;

    // No active policy → empty state.
    if (!has_active_policy) {
        return (
            <div className="mb-card" data-testid="retention-forecast-empty">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#5b5ef0]/15 text-[#5b5ef0]">
                            <TrendingDown size={16} />
                        </div>
                        <div>
                            <h3 className="font-display text-base font-bold">Storage savings forecast</h3>
                            <p className="mt-0.5 text-[12px] text-muted-foreground">
                                You don&apos;t have any retention policy active yet. Configure one to start projecting how much DB and GridFS space you&apos;ll reclaim each month.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Link
                            to="/retention"
                            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest hover:border-foreground/40"
                            data-testid="retention-forecast-configure-link"
                        >
                            Configure retention <ArrowUpRight size={12} />
                        </Link>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={applyDefaults}
                                disabled={applying}
                                data-testid="retention-forecast-apply-defaults-btn"
                                className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white shadow disabled:opacity-60"
                            >
                                <Sparkles size={12} /> Apply SOC2 defaults
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const totalAt90 = totals.records_at[totals.records_at.length - 1] || 0;
    const bytesAt90 = totals.approx_bytes_at[totals.approx_bytes_at.length - 1] || 0;

    return (
        <div className="mb-card" data-testid="retention-forecast">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#5b5ef0]/15 text-[#5b5ef0]">
                        <TrendingDown size={16} />
                    </div>
                    <div>
                        <h3 className="font-display text-base font-bold">Storage savings forecast</h3>
                        <p className="mt-0.5 text-[12px] text-muted-foreground">
                            With the current policy, your tenant will reclaim{" "}
                            <span className="font-semibold text-foreground" data-testid="retention-forecast-headline-bytes">
                                ~{formatBytes(bytesAt90)}
                            </span>{" "}
                            across{" "}
                            <span className="font-semibold text-foreground tabular-nums" data-testid="retention-forecast-headline-records">
                                {totalAt90.toLocaleString()}
                            </span>{" "}
                            records over the next 90 days.
                        </p>
                    </div>
                </div>
                <Link
                    to="/retention"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5b5ef0]"
                    data-testid="retention-forecast-policy-link"
                >
                    Manage policy <ArrowUpRight size={12} />
                </Link>
            </div>

            {/* Horizon cards */}
            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="retention-forecast-horizons">
                {horizons.map((h, i) => (
                    <HorizonCard
                        key={h}
                        days={h}
                        records={totals.records_at[i]}
                        bytes={totals.approx_bytes_at[i]}
                        emphasis={i === horizons.length - 1}
                        testId={`retention-forecast-horizon-${h}`}
                    />
                ))}
            </div>

            {/* Per-class breakdown */}
            <div className="mt-4 border-t border-border pt-3">
                <div className="mb-2 flex items-center justify-between">
                    <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                        Per-class breakdown @ 90d
                    </div>
                </div>
                <div className="space-y-1.5">
                    {Object.entries(per_class)
                        .filter(([, d]) => d.enabled && (d.would_delete_at[horizons.length - 1] || 0) > 0)
                        .sort((a, b) => (b[1].approx_bytes_at[horizons.length - 1] || 0) - (a[1].approx_bytes_at[horizons.length - 1] || 0))
                        .map(([k, d]) => {
                            const pct = bytesAt90
                                ? Math.max(2, ((d.approx_bytes_at[horizons.length - 1] || 0) / bytesAt90) * 100)
                                : 0;
                            return (
                                <ClassRow
                                    key={k}
                                    label={class_meta?.[k]?.label || k}
                                    tint={CLASS_TINTS[k] || "#5b5ef0"}
                                    records={d.would_delete_at[horizons.length - 1] || 0}
                                    bytes={d.approx_bytes_at[horizons.length - 1] || 0}
                                    pct={pct}
                                    testId={`retention-forecast-class-${k}`}
                                />
                            );
                        })}
                    {Object.entries(per_class).filter(([, d]) => d.enabled && (d.would_delete_at[horizons.length - 1] || 0) > 0).length === 0 && (
                        <div className="text-[11px] text-muted-foreground">
                            No class projects deletions in the 90-day window — your data is already inside the configured retention bounds.
                        </div>
                    )}
                </div>
            </div>

            {/* SOC2 upsell — only when the operator hasn't already applied them */}
            {suggested_defaults_available && isAdmin && (
                <div
                    className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#5b5ef0]/40 bg-gradient-to-r from-[#5b5ef0]/5 to-[#00C2FF]/5 p-3"
                    data-testid="retention-forecast-defaults-banner"
                >
                    <div className="flex items-start gap-2">
                        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#5b5ef0]" />
                        <div className="text-[12px]">
                            <div className="font-semibold">SOC2-friendly defaults available</div>
                            <div className="text-muted-foreground">
                                One click applies a 365-day audit window + sensible cleanup for ephemeral data — without touching any class you&apos;ve already fine-tuned more aggressively.
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={applyDefaults}
                        disabled={applying}
                        data-testid="retention-forecast-apply-defaults-btn"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white shadow disabled:opacity-60"
                    >
                        <Sparkles size={12} /> {applying ? "Applying…" : "Apply SOC2 defaults"}
                    </button>
                </div>
            )}
        </div>
    );
}

function HorizonCard({ days, records, bytes, emphasis, testId }) {
    return (
        <div
            className={[
                "rounded-lg border p-3 transition-colors",
                emphasis
                    ? "border-[#5b5ef0]/50 bg-[#5b5ef0]/5"
                    : "border-border bg-background/40",
            ].join(" ")}
            data-testid={testId}
        >
            <div className="font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                @ {days} days
            </div>
            <div className="mt-1 font-display text-lg font-bold tabular-nums">
                {formatBytes(bytes || 0)}
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
                {(records || 0).toLocaleString()} record{records === 1 ? "" : "s"}
            </div>
        </div>
    );
}

function ClassRow({ label, tint, records, bytes, pct, testId }) {
    return (
        <div className="flex items-center gap-3" data-testid={testId}>
            <div className="w-32 shrink-0 truncate text-[12px] text-muted-foreground" title={label}>
                {label}
            </div>
            <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted/30">
                <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: tint }}
                />
            </div>
            <div className="w-20 shrink-0 text-right font-mono text-[11px] tabular-nums">
                {formatBytes(bytes)}
            </div>
            <div className="w-16 shrink-0 text-right text-[11px] text-muted-foreground tabular-nums">
                {records.toLocaleString()}
            </div>
        </div>
    );
}
